import { Flex, Image, Loader, Text, View } from "@aws-amplify/ui-react"
import { useContext, useState } from "react"
import ConfigurationContext from "../context/ConfigurationContext.js"
import LivenessSessionStatus from "../domain/LivenessSessionStatus.js"
import Translation from "../i18n/Translation.js"
import type { Branding } from "../schemata/biometry/Branding.js"
import style from "../style/VerificationCompletion.module.css"
import BrandedFooter from "./BrandedFooter.js"
import BrandedHeader from "./BrandedHeader.js"

type Properties = Readonly<{
	branding: Branding | undefined
	completionText: Record<string, string>
	isLive: boolean
	status: LivenessSessionStatus
}>

/**
 * @description
 *	The result screen of a verification, shared by Face Liveness Detection and
 *	by Face Signature: the sentence of the outcome is the only difference
 *	between the two, and it arrives as a property.
 */
export default function VerificationCompletion({ branding, completionText, isLive, status }: Properties) {
	const [effectiveStatus, setEffectiveStatus] = useState<LivenessSessionStatus | undefined>(undefined)
	const configuration = useContext(ConfigurationContext)
	if (effectiveStatus === undefined) {
		setEffectiveStatus(
			status !== LivenessSessionStatus.SUCCEEDED
				? status
				: isLive
					? LivenessSessionStatus.SUCCEEDED
					: LivenessSessionStatus.FAILED
		)
	}
	return (
		<View className={style["verification-completion-view"]}>
			<BrandedHeader branding={branding} />
			<Flex direction="column" className={style["verification-completion-flex-column"]}>
				{effectiveStatus ? (
					<>
						<Image
							alt={effectiveStatus}
							className={style["verification-completion-icon"]}
							src={`/icon/${effectiveStatus.toLowerCase()}.gif`}
						/>
						<Text as="p" className={style["verification-completion-text"]}>
							{completionText[effectiveStatus]}
						</Text>
						<Text as="p" className={style["verification-completion-text-bold"]}>
							{Translation[configuration.DEFAULT_LOCALE].canCloseAfterCompletion!}
						</Text>
					</>
				) : (
					<Loader className={style["verification-completion-loader"]} />
				)}
			</Flex>
			<BrandedFooter />
		</View>
	)
}
